import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { format } from 'date-fns';
import { io, Socket } from 'socket.io-client';
import socketService from '../services/socketService';
import { debounce } from 'lodash';
import Header from '../components/Header'; // Assuming Header component exists and is styled
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import SintisLogo from '../assets/Sintis_Logo.png';
import SintisIcon from '../assets/Sintis_Icon.png';
import {
  Search, MessageSquarePlus,
  AlertCircle, MessageSquare, Loader2,
  XCircle,
  Users,
  Tag,
  LayoutGrid,
} from 'lucide-react';
import VirtualizedConversaList from '../components/VirtualizedConversaList';
import AtendimentoPage from './AtendimentoPage';
import { renderTextWithLinks } from '../utils/renderTextWithLinks';
import { renderEmojiWithFallback } from '../utils/emojiUtils';
import { DraftManager } from '../utils/draftManager';
import { ConversaResumo } from '../types/conversa';
import { useTabCounter } from '../hooks/useTabCounter';
import { TagBadge } from '../components/ui/tag-badge';
import { SelectWithSearch, SelectOption } from '../components/ui/select-with-search';
import ConversaListItem from '../components/ConversaListItem';
import NovoChatModal from '../components/modals/NovoChatModal';
import KanbanBoard from '../components/kanban/KanbanBoard';
import { getUltimaMensagemComRascunho, mapBackendConversaToResumo, getFileUrl, getInitials, API_BASE_URL } from '../utils/dashboardHelpers';

// Adicionar CSS para animação do placeholder
const marqueeStyle = `
  @keyframes marquee-text {
    0% { transform: translateX(0); }
    100% { transform: translateX(-100%); }
  }
  
  .search-input-marquee-text {
    display: inline-block;
    padding-left: 100%;
    animation: marquee-text 15s linear infinite;
  }
`;

const pickDefined = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;
    if (value instanceof Date && isNaN(value.getTime())) continue;
    result[key] = value;
  }
  return result;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const mountedRef = useRef(true);
  const fetchRequestIdRef = useRef(0);

  // Track component mount state to prevent state updates after unmount/navigation
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Injetar CSS para animação do placeholder
  useEffect(() => {
    const styleId = 'marquee-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = marqueeStyle;
      document.head.appendChild(style);
    }
  }, []);
  const [conversas, setConversas] = useState<ConversaResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingAttendance, setStartingAttendance] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showGroupMessages, setShowGroupMessages] = useState(true); // Mostrar mensagens de grupo
  const [activeTab, setActiveTab] = useState<'aberto' | 'pendente' | 'fechado'>('aberto');
  const [user, setUser] = useState<any>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [newChatJid, setNewChatJid] = useState('');
  const [isNewChatDialogOpen, setIsNewChatDialogOpen] = useState(false);
  // New Chat modal state: contacts list and search
  const [modalContatos, setModalContatos] = useState<any[]>([]);
  const [modalContatosLoading, setModalContatosLoading] = useState(false);
  const [modalContatoSearch, setModalContatoSearch] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const socketCallbacksRef = useRef<any>(null);
  const [countAbertos, setCountAbertos] = useState(0);
  const [countPendentes, setCountPendentes] = useState(0);
  const [countFechados, setCountFechados] = useState(0);
  const [conversaSelecionada, setConversaSelecionada] = useState<ConversaResumo | null>(null);
  const [refreshTime, setRefreshTime] = useState(Date.now());
  const [logo, setLogo] = useState('');
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageRef = useRef(1);
  const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001')?.replace(/\/api$/, '');

  // Chat configuration state
  const [chatConfig, setChatConfig] = useState<any>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferToUser, setTransferToUser] = useState('');
  const [transferToQueue, setTransferToQueue] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [availableQueues, setAvailableQueues] = useState<any[]>([]);
  const [transferTarget, setTransferTarget] = useState<ConversaResumo | null>(null);
  const [loadingTransfer, setLoadingTransfer] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Instance selection state for new chat modal
  const [availableInstances, setAvailableInstances] = useState<any[]>([]);
  const [selectedInstance, setSelectedInstance] = useState('');

  // Unread messages filter state
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');

  // Message search states
  const [searchMessagesResults, setSearchMessagesResults] = useState<any[]>([]);
  const [searchMessagesLoading, setSearchMessagesLoading] = useState(false);
  const [searchMessagesError, setSearchMessagesError] = useState<string | null>(null);
  const [showMessagesSearch, setShowMessagesSearch] = useState(false);
  const [searchMessagesPagination, setSearchMessagesPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  // Auxiliar: tenta obter conversa existente ou criar nova por telefone
  const getOrCreateChatByTelefone = useCallback(async (telefone: string, instanciaId?: string, isContact?: boolean): Promise<ConversaResumo | null> => {
    const cleanTelefone = (telefone || '').trim();
    if (!cleanTelefone) return null;

    // Validação genérica (E.164 sem o '+'): mínimo 8 e máximo 15 dígitos
    const onlyDigits = cleanTelefone.replace(/\D/g, '');

    // Verificar se é um grupo do WhatsApp (@g.us ou 16+ dígitos)
    const isGroup = cleanTelefone.includes('@g.us') || onlyDigits.length > 15;

    // Se não for grupo, validar como número de telefone
    if (!isGroup) {
      // (permite números internacionais; a definição do DDI fica a cargo do usuário)
      if (onlyDigits.length < 8 || onlyDigits.length > 15) return null;
    }

    // 1) Verificar localmente primeiro (rápido)
    const local = conversas.find(c => {
      const matchesPhone = isGroup
        ? (c.remoteJid === cleanTelefone || c.contato?.telefone === cleanTelefone)
        : (() => { const d = cleanTelefone.replace(/\D/g, ''); return c.contato?.telefone?.replace(/\D/g, '') === d || c.remoteJid?.includes(d); })();
      if (!matchesPhone) return false;
      if (c.statusTicket !== 'aberto' && c.statusTicket !== 'pendente') return false;
      // Se instância foi selecionada, só retornar ticket dessa instância
      if (instanciaId) {
        return c.instancia?.id === instanciaId || c.instanciaUazapi?.id === instanciaId || c.instanciaMeta?.id === instanciaId;
      }
      return true;
    });
    if (local) return local;

    // 2) Chamar backend unificado: /chats/find-or-create
    try {
      const payload: any = { telefone: cleanTelefone };
      if (instanciaId) payload.instanciaId = instanciaId;
      const resp = await api.post('/chats/find-or-create', payload);
      const createdOrFound = resp?.data;
      if (!createdOrFound) return null;

      // Se o atendimento já estiver com outro atendente
      if (createdOrFound.alreadyAssigned) {
        toast({
          title: createdOrFound.message || t('dashboard.clientHasOpenAttendance'),
          description: t('dashboard.inAttendanceWith', { atendente: createdOrFound.assignedTo || t('dashboard.otherAttendant') }),
          variant: 'destructive',
        });
        return null;
      }

      // Se já houver atendimento aberto em outra instância
      if (createdOrFound.openOnOtherInstance) {
        const desc = createdOrFound.assignedTo
          ? t('dashboard.openOnOtherInstanceWith', { instance: createdOrFound.instanceName, attendant: createdOrFound.assignedTo })
          : t('dashboard.openOnOtherInstance', { instance: createdOrFound.instanceName });
        toast({
          title: createdOrFound.message || t('dashboard.contactHasOpenAttendance'),
          description: desc,
          variant: 'destructive',
        });
        return null;
      }

      // Se já vier completo, mapeia diretamente; caso contrário busca por ID
      const id = createdOrFound?.id;
      if (id) {
        try {
          const full = await api.get(`/chats/${id}`);
          return mapBackendConversaToResumo(full.data);
        } catch {
          return mapBackendConversaToResumo(createdOrFound);
        }
      }
      return mapBackendConversaToResumo(createdOrFound);
    } catch (err: any) {
      console.error('Erro em find-or-create:', err);
      const msg = err?.response?.data?.message || t('dashboard.couldNotOpenCreateAttendance');
      toast({ title: t('common.error'), description: msg, variant: 'destructive' });
      return null;
    }
  }, [conversas]);

  // Handle URL params
  useEffect(() => {
    const ticketId = searchParams.get('ticketId');
    if (ticketId && conversas.length > 0) {
      const chat = conversas.find(c => c.id === ticketId);
      if (chat && chat.id !== conversaSelecionada?.id) {
        setConversaSelecionada(chat);
        setShowChat(true);
      } else if (!chat && conversaSelecionada?.id !== ticketId) {
        // Conversa recém-criada (ex.: grupo novo) que ainda não está na lista carregada
        api.get(`/chats/${ticketId}`)
          .then(resp => {
            setConversaSelecionada(mapBackendConversaToResumo(resp.data));
            setShowChat(true);
          })
          .catch(() => {});
      }
    }
  }, [searchParams, conversas, conversaSelecionada?.id]);

  const handleStartNewChat = async (telefoneOverride?: string) => {
    // Usa telefoneOverride se fornecido, senão newChatJid, senão modalContatoSearch
    const telefoneInput = telefoneOverride || newChatJid || (modalContatoSearch && /^[+]?\d+$/.test(modalContatoSearch) ? modalContatoSearch : '');

    if (!telefoneInput) {
      toast({ title: t('common.error'), description: t('dashboard.enterContactPhone'), variant: 'destructive' });
      return;
    }

    const cleanTelefone = telefoneInput.trim();
    if (!cleanTelefone) {
      toast({ title: t('common.error'), description: t('dashboard.invalidPhone'), variant: 'destructive' });
      return;
    }

    if (!selectedInstance) {
      toast({ title: t('common.error'), description: t('dashboard.selectInstance'), variant: 'destructive' });
      return;
    }

    // Verificar se é um grupo do WhatsApp (@g.us ou 16+ dígitos)
    const isGroup = cleanTelefone.includes('@g.us') || cleanTelefone.replace(/\D/g, '').length > 15;

    // Se não for grupo, validar como número de telefone
    if (!isGroup) {
      const onlyDigits = cleanTelefone.replace(/\D/g, '');
      // Validação genérica (E.164 sem o '+'): 8..15 dígitos
      if (onlyDigits.length < 8 || onlyDigits.length > 15) {
        toast({ title: t('common.error'), description: t('dashboard.phoneDigitsRange'), variant: 'destructive' });
        return;
      }
    }

    try {
      const conversaResumo = await getOrCreateChatByTelefone(cleanTelefone, selectedInstance);
      if (!conversaResumo) {
        toast({ title: t('common.error'), description: t('dashboard.couldNotOpenAttendance'), variant: 'destructive' });
        return;
      }
      setIsNewChatDialogOpen(false);
      setNewChatJid('');
      setModalContatoSearch('');
      setSelectedInstance('');

      // Se a conversa estiver como pendente, inicia o atendimento automaticamente
      if (conversaResumo.statusTicket === 'pendente') {
        try {
          await api.put(`/chats/${conversaResumo.id}/status?status=aberto`);
          conversaResumo.statusTicket = 'aberto';
          conversaResumo.status = 'aberto';
          setConversas(prev => prev.map(c =>
            c.id === conversaResumo.id
              ? { ...c, statusTicket: 'aberto', status: 'aberto' }
              : c
          ));
        } catch {
          // Se falhar, continua mesmo assim
        }
      }

      handleOpenConversa(conversaResumo);
      setActiveTab('aberto');
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('dashboard.startAttendanceError'),
        variant: 'destructive',
      });
    }
  };

  const handleStartNewChatFromContato = async (telefone: string) => {
    const cleanTelefone = (telefone || '').trim();
    if (!cleanTelefone) {
      toast({ title: t('common.error'), description: t('dashboard.invalidContactPhone'), variant: 'destructive' });
      return;
    }

    if (!selectedInstance) {
      // Sem instância selecionada: preenche o campo de telefone e abre o modal
      // para o usuário escolher a instância e clicar em "Iniciar Conversa"
      setNewChatJid(cleanTelefone);
      setModalContatoSearch(cleanTelefone);
      setIsNewChatDialogOpen(true);
      return;
    }

    // Já tem instância selecionada: inicia o atendimento direto
    await handleStartNewChat(cleanTelefone);
  };

  const handleNewChatDialogChange = useCallback((open: boolean) => {
    setIsNewChatDialogOpen(open);
    if (!open) {
      // Reset state when modal closes
      setSelectedInstance('');
      setModalContatoSearch('');
      setNewChatJid('');
      setModalContatos([]);
      setModalContatosLoading(false);
    }
  }, []);

  // --- Carregar Usuário ---
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));

      // Limpar rascunhos antigos ao carregar o Dashboard
      DraftManager.cleanOldDrafts();
    } else {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchLogo = useCallback(async () => {
    try {
      const response = await api.get('/empresas/logo');
      const logoData = response.data.logo;
      const nomeData = response.data.nome;
      setLogo(logoData);
      setNomeEmpresa(nomeData);
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
      return null;
    }
  }, []);

  const fetchAvailableTags = useCallback(async () => {
    try {
      const response = await api.get('/tags');
      const items = response.data?.data || response.data?.tags || [];
      setAvailableTags(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error('Erro ao carregar tags disponíveis:', error);
      setAvailableTags([]);
    }
  }, []);

  // Fetch instances separately
  const fetchAvailableInstances = useCallback(async () => {
    if (!user?.empresaId) return;

    try {
      const instancesResponse = await api.get('/instancias');
      const instances = Array.isArray(instancesResponse.data) ? instancesResponse.data : (instancesResponse.data?.instancias || []);
      setAvailableInstances(instances);
    } catch (error) {
      console.error('Erro ao carregar instâncias:', error);
      setAvailableInstances([]);
    }
  }, [user?.empresaId]);

  // Listen for instance refresh events
  useEffect(() => {
    const handleRefreshInstances = () => {
      fetchAvailableInstances();
    };

    window.addEventListener('refreshInstances', handleRefreshInstances);

    return () => {
      window.removeEventListener('refreshInstances', handleRefreshInstances);
    };
  }, []); // Removido fetchAvailableInstances da dependência para evitar re-renders

  // Auto-select única instância quando o modal abrir (ou instâncias carregarem)
  useEffect(() => {
    if (isNewChatDialogOpen && availableInstances.length === 1 && !selectedInstance) {
      setSelectedInstance(availableInstances[0].id);
    }
  }, [isNewChatDialogOpen, availableInstances, selectedInstance]);

  const fetchChatConfig = useCallback(async () => {
    try {
      const empresaId = user?.role === 'SuperAdmin' ? null : user?.empresaId;
      if (!empresaId) return;

      const response = await api.get(`/configuracoes-chat/empresa/${empresaId}`);
      setChatConfig(response.data.configuracao);
    } catch (error) {
      console.error('Erro ao carregar configurações do chat:', error);
    }
  }, [user]);

  // --- Carregar Conversas do banco (AJUSTADO) ---
  const fetchConversasFromBackend = useCallback(async (page = 1, append = false) => {
    if (!mountedRef.current) return;
    const fetchId = ++fetchRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      // Get current values directly inside the function to avoid circular dependencies
      const currentActiveTab = activeTab;
      const currentSearchTerm = searchTerm;
      const currentShowGroupMessages = showGroupMessages;
      const currentChatConfig = chatConfig;
      const currentSelectedTags = selectedTags;

      // Para contadores, não passar status para obter todos os status
      const statusParam = currentSearchTerm ? '' : `status=${currentActiveTab}&`;
      const groupParam = currentShowGroupMessages === false ? `isGrupo=false&` : '';
      const groupByContactParam = currentChatConfig?.agruparChatsPorContatos ? 'agruparPorContato=true&' : '';
      const tagsParam = currentSelectedTags.length > 0 ? `tags=${currentSelectedTags.join(',')}&` : '';
      const searchParam = currentSearchTerm ? `search=${encodeURIComponent(currentSearchTerm)}&` : '';
      const unreadParam = showUnreadOnly ? 'naoLidas=true&' : '';
      // Pesquisa mantém o tamanho de página padrão do backend (não afetada pela configuração do admin)
      const pageSize = currentSearchTerm ? 50 : (currentChatConfig?.tamanhoPaginaAtendimento || 50);
      const limitParam = `limit=${pageSize}&`;

      const response = await api.get<{
        chats: any[];
        totalCount: number;
        page: number;
        totalPages: number;
        counts: {
          aberto: number;
          pendente: number;
          fechado: number;
        };
        totalUnread?: number;
      }>(`/chats?${statusParam}${groupParam}${groupByContactParam}${tagsParam}${searchParam}${unreadParam}${limitParam}page=${page}`);

      if (fetchId !== fetchRequestIdRef.current) return;

      const mappedConversas = response.data.chats.map(mapBackendConversaToResumo);
      const sortedConversas = mappedConversas.sort(
        (a, b) => {
          const dateA = a.dataAtualizacao instanceof Date ? a.dataAtualizacao : new Date(a.dataAtualizacao);
          const dateB = b.dataAtualizacao instanceof Date ? b.dataAtualizacao : new Date(b.dataAtualizacao);
          return dateB.getTime() - dateA.getTime();
        }
      );

      console.log(`[Dashboard] fetch API returned ${response.data.chats.length} items for page ${page}`);

      if (append) {
        setConversas(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newConversas = sortedConversas.filter(c => !existingIds.has(c.id));
          console.log(`[Dashboard] Append mode: prev=${prev.length}, received=${sortedConversas.length}, new=${newConversas.length}`);
          return [...prev, ...newConversas];
        });
      } else {
        setConversas(sortedConversas);
      }

      // Atualizar contadores globais (sempre usar countsData do backend, que reflete TODAS as páginas)
      const countsData = response.data.counts;
      if (countsData) {
        setCountAbertos(countsData.aberto || 0);
        setCountPendentes(countsData.pendente || 0);
        setCountFechados(countsData.fechado || 0);
      }

      // Total de mensagens não lidas para o contador da aba do navegador
      if (response.data.totalUnread !== undefined) {
        setTotalUnread(response.data.totalUnread);
      }

      setHasMore(page < response.data.totalPages);
      setCurrentPage(page);
      currentPageRef.current = page;
    } catch (err: any) {
      if (fetchId !== fetchRequestIdRef.current) return;
      console.error("Erro ao carregar conversas:", err);
      const errorMsg = err.response?.data?.message || t('dashboard.loadConversaError');
      setError(errorMsg);
      toast({
        title: t('dashboard.loadConversaError'),
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      if (fetchId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeTab, searchTerm, showGroupMessages, chatConfig, selectedTags, showUnreadOnly]);

  // Carregar config, logo, tags e instâncias
  useEffect(() => {
    if (user) {
      fetchChatConfig();
      fetchLogo();
      fetchAvailableTags();
      if (user?.empresaId) {
        fetchAvailableInstances();
      }
    }
  }, [user, fetchLogo, fetchChatConfig, fetchAvailableTags, fetchAvailableInstances]);

  // Hook para gerenciar contador de mensagens não lidas na aba do navegador
  const { forceUpdate } = useTabCounter({
    originalTitle: 'SinTicket',
    includeGroups: chatConfig?.contadorIncluirGrupos ?? false,
    separator: ' - '
  });

  // Atualizar contador de mensagens não lidas na aba do navegador via heartbeat
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await api.get('/mensagens/contar-nao-lidas');
        const count = response.data.count;
        forceUpdate(count);
      } catch (error) {
        console.error('Error fetching unread messages:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [activeTab, forceUpdate]);

  // Fetch conversations when filters change
  useEffect(() => {
    if (user && chatConfig) {
      setCurrentPage(1);
      currentPageRef.current = 1;
      fetchConversasFromBackend();
    }
  }, [activeTab, user, showGroupMessages, chatConfig, searchTerm, selectedTags, showUnreadOnly]);

  // Load users and queues for transfer (called when opening modal)
  const loadTransferData = useCallback(async () => {
    if (!user?.empresaId) return;
    setLoadingTransfer(true);
    try {
      const [usersResponse, queuesResponse] = await Promise.all([
        api.get('/usuarios/transferencia'),
        api.get('/filas/transferencia')
      ]);
      const users = Array.isArray(usersResponse.data.usuarios) ? usersResponse.data.usuarios : [];
      const queues = Array.isArray(queuesResponse.data.filas) ? queuesResponse.data.filas : [];
      setAvailableUsers(users);
      setAvailableQueues(queues);
    } catch (error) {
      console.error('Erro ao carregar usuários e filas:', error);
      setAvailableUsers([]);
      setAvailableQueues([]);
    } finally {
      setLoadingTransfer(false);
    }
  }, [user?.empresaId]);

  // Load contacts when opening the New Chat dialog or searching inside it
  const fetchModalContatos = useCallback(async (search: string) => {
    setModalContatosLoading(true);
    try {
      const params: any = { search: search || undefined, limit: 50 };
      const resp = await api.get('/contatos', { params });
      const data = resp?.data;
      const list = Array.isArray(data)
        ? data
        : (data?.contatos || data?.items || []);
      setModalContatos(list || []);
    } catch (e) {
      console.error('Erro ao carregar contatos para novo chat', e);
      setModalContatos([]);
    } finally {
      setModalContatosLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isNewChatDialogOpen) {
      fetchModalContatos(modalContatoSearch);
    }
  }, [isNewChatDialogOpen, modalContatoSearch, fetchModalContatos]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTime(Date.now());
    }, 60000); // 1 minute
    return () => clearInterval(interval);
  }, []);

  const showNotification = useCallback(async (title: string, body: string) => {
    const hasPermission = await requestNotificationPermission();
    if (hasPermission) {
      new Notification(title, {
        body: body,
        icon: SintisIcon // Usar o logo como ícone
        // tag: 'nova-mensagem' // Para agrupar ou substituir notificações
      });
      // TODO: Adicionar à lista de notificações internas no popover
    } else {
      console.warn("Permissão para notificações negada ou não suportada.");
    }
  }, []);

  // Função para atualizar a lista de conversas com debounce
  const updateConversations = useCallback(
    debounce(async () => {
      if (!mountedRef.current) return;
      // Apenas atualizar contadores para evitar desaparecimento da lista
      // A lista já é atualizada em tempo real via socket events
      try {
        // Get current values directly inside the function
        const currentActiveTab = activeTab;
        const currentSearchTerm = searchTerm;
        const currentShowGroupMessages = showGroupMessages;
        const currentChatConfig = chatConfig;
        const currentSelectedTags = selectedTags;

        const statusParam = currentSearchTerm ? '' : `status=${currentActiveTab}&`;
        const groupParam = currentShowGroupMessages === false ? `isGrupo=false&` : '';
        const groupByContactParam = currentChatConfig?.agruparChatsPorContatos ? 'agruparPorContato=true&' : '';
        const searchParam = currentSearchTerm ? `search=${encodeURIComponent(currentSearchTerm)}&` : '';
        const tagsParam = currentSelectedTags.length > 0 ? `tags=${currentSelectedTags.join(',')}&` : '';
        const unreadParam = showUnreadOnly ? 'naoLidas=true&' : '';

        const response = await api.get(`/chats?${statusParam}${groupParam}${groupByContactParam}${searchParam}${tagsParam}${unreadParam}countsOnly=true&page=1`);

        if (response.data.counts) {
          setCountAbertos(response.data.counts.aberto || 0);
          setCountPendentes(response.data.counts.pendente || 0);
          setCountFechados(response.data.counts.fechado || 0);
        }
        if (response.data.totalUnread !== undefined) {
          setTotalUnread(response.data.totalUnread);
        }
      } catch (error) {
        console.error('Erro ao atualizar contadores:', error);
      }
    }, 300), // Reduzido de 1000ms para 300ms para atualização mais rápida
    [activeTab, searchTerm, showGroupMessages, chatConfig, selectedTags, showUnreadOnly]
  );

  // --- Busca de mensagens ---
  const handleSearchMessages = useCallback(
    debounce(async (term: string, page = 1) => {
      if (!term.trim()) {
        setSearchMessagesResults([]);
        setSearchMessagesError(null);
        setShowMessagesSearch(false);
        setSearchMessagesPagination({
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
          hasMore: false
        });
        return;
      }
      setSearchMessagesLoading(true);
      setSearchMessagesError(null);
      setShowMessagesSearch(true);
      try {
        // Buscar em todas as conversas do usuário
        const resp = await api.get('/chats/search-all-messages', {
          params: {
            term: term.trim(),
            page: page,
            limit: 100
          }
        });

        setSearchMessagesResults(resp.data.messages || []);
        setSearchMessagesPagination(resp.data.pagination || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
          hasMore: false
        });
      } catch (err: any) {
        setSearchMessagesError(t('dashboard.searchMessagesError'));
        setSearchMessagesResults([]);
        toast({ title: t('common.error'), description: t('dashboard.searchMessagesError'), variant: "destructive" });
      } finally {
        setSearchMessagesLoading(false);
      }
    }, 2000),
    [toast] // Adicionando toast como dependência
  );

  // Função para carregar mais mensagens da busca
  const loadMoreSearchMessages = useCallback(async () => {
    if (!searchMessagesPagination.hasMore || searchMessagesLoading) return;

    const nextPage = searchMessagesPagination.page + 1;
    const currentTerm = searchTerm;

    if (currentTerm) {
      try {
        setSearchMessagesLoading(true);

        // Buscar em todas as conversas do usuário
        const resp = await api.get('/chats/search-all-messages', {
          params: {
            term: currentTerm.trim(),
            page: nextPage,
            limit: 100
          }
        });

        // Concatenar com resultados anteriores em vez de sobrescrever
        const newMessages = resp.data.messages || [];
        setSearchMessagesResults(prev => [...prev, ...newMessages]);
        setSearchMessagesPagination(resp.data.pagination || {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
          hasMore: false
        });
      } catch (err: any) {
        console.error('Erro ao carregar mais mensagens:', err);
        toast({ title: t('common.error'), description: t('dashboard.searchMoreMessagesError'), variant: "destructive" });
      } finally {
        setSearchMessagesLoading(false);
      }
    }
  }, [searchMessagesPagination.hasMore, searchMessagesLoading, searchTerm]);

  // Função utilitária para destacar termo buscado
  function highlightTerm(text: string, term: string) {
    if (!term) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: text.replace(regex, '<mark class="bg-yellow-200 text-black rounded px-1">$1</mark>')
        }}
      />
    );
  }

  // Função para abrir conversa a partir de uma mensagem encontrada
  const handleOpenConversationFromMessage = async (message: any) => {
    let conversa = conversas.find(c => c.id === message.conversaId);

    // Se não encontrou na lista atual (outra aba/filtro), busca da API
    if (!conversa) {
      try {
        const resp = await api.get(`/chats/${message.conversaId}`);
        conversa = mapBackendConversaToResumo(resp.data);
        setConversas(prev => [conversa!, ...prev]);
      } catch {
        toast({ title: t('common.error'), description: t('dashboard.conversationNotFound'), variant: 'destructive' });
        return;
      }
    }

    // Limpa estados de busca
    setShowMessagesSearch(false);
    setSearchMessagesResults([]);
    setSearchTerm('');

    // Abre a conversa e ativa o chat
    setConversaSelecionada(conversa!);
    setShowChat(true);

    // Armazena a mensagem alvo para navegação posterior
    localStorage.setItem('targetMessageId', message.id);
    localStorage.setItem('targetApiMessageId', message.apiMessageId || '');
    localStorage.setItem('targetEvolutionMessageId', message.evolutionMessageId || '');

    // Atualiza conversas para marcar como lida
    setConversas(prev => prev.map(c => c.id === conversa!.id ? { ...c, naoLidas: 0 } : c));
  };

  // --- Busca de mensagens automática ---
  useEffect(() => {
    if (searchTerm.trim().length > 0) {
      // Resetar paginação para página 1 quando mudar o termo
      setSearchMessagesPagination(prev => ({
        ...prev,
        page: 1
      }));
      handleSearchMessages(searchTerm, 1);
    } else {
      // Limpa completamente a visualização quando o campo está vazio
      setSearchMessagesResults([]);
      setSearchMessagesError(null);
      setSearchMessagesLoading(false);
      setShowMessagesSearch(false);
      setSearchMessagesPagination({
        page: 1,
        limit: 100,
        total: 0,
        totalPages: 0,
        hasMore: false
      });
    }
  }, [searchTerm, handleSearchMessages]); // Added missing closing bracket and dependencies

  // --- Configuração do Socket.IO para atualização em tempo real ---
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const setupSocketListeners = async () => {
      try {
        const socket = await socketService.connect(token);
        setSocketConnected(true);

        // Entrar nas salas da empresa e da conversa (se houver uma selecionada)
        if (user?.empresaId) {
          socketService.joinEmpresa(user.empresaId);
        }
        if (conversaSelecionada?.id) {
          socketService.joinConversa(conversaSelecionada.id);
        }

        // Event listeners
        const handleChatListUpdated = () => {
          updateConversations();
        };

        const handleConversaAtualizada = (conversaAtualizada: any) => {
          setConversas(prev => {
            const idx = prev.findIndex(c => c.id === conversaAtualizada.id);
            let novasConversas;
            if (idx > -1) {
              novasConversas = [...prev];
              const existing = prev[idx];
              const conversaResumo = mapBackendConversaToResumo(conversaAtualizada);
              if (conversaResumo.ultimaMensagem && existing.ultimaMensagem) {
                conversaResumo.ultimaMensagem = {
                  ...existing.ultimaMensagem,
                  ...pickDefined(conversaResumo.ultimaMensagem),
                };
              }
              novasConversas[idx] = { ...existing, ...pickDefined(conversaResumo) };
            } else {
              novasConversas = [mapBackendConversaToResumo(conversaAtualizada), ...prev];
            }
            return novasConversas.sort((a, b) => {
              if (a.fixada && !b.fixada) return -1;
              if (!a.fixada && b.fixada) return 1;
              const dateA = a.dataAtualizacao instanceof Date ? a.dataAtualizacao : new Date(a.dataAtualizacao);
              const dateB = b.dataAtualizacao instanceof Date ? b.dataAtualizacao : new Date(b.dataAtualizacao);
              return dateB.getTime() - dateA.getTime();
            });
          });

          // Mostra notificação para novas mensagens do cliente
          if (
            conversaAtualizada.ultimaMensagem &&
            conversaAtualizada.ultimaMensagem.remetenteTipo === 'cliente' &&
            (conversaAtualizada.naoLidas ?? 0) > 0
          ) {
            showNotification(
              `Nova mensagem de ${conversaAtualizada.nome || conversaAtualizada.contato?.nome || 'Desconhecido'}`,
              conversaAtualizada.ultimaMensagem.conteudo || '[Mensagem]'
            );
          }

          // Atualiza os contadores de status em tempo real
          updateConversations();
        };

        const handleStatusMensagem = (statusUpdate: any) => {
          // Atualiza o status da última mensagem se corresponder
          setConversas(prev => {
            const updated = prev.map(c => {
              // Verifica se a conversa tem uma última mensagem e se o ID corresponde
              if (c.ultimaMensagem && (
                c.ultimaMensagem.id === statusUpdate.id ||
                c.ultimaMensagem.apiMessageId === statusUpdate.apiMessageId ||
                c.ultimaMensagem.evolutionMessageId === statusUpdate.apiMessageId
              )) {
                console.log('Atualizando status da conversa:', c.id, 'de:', c.ultimaMensagem.statusEntrega, 'para:', statusUpdate.statusEntrega);
                return {
                  ...c,
                  ultimaMensagem: {
                    ...c.ultimaMensagem,
                    statusEntrega: statusUpdate.statusEntrega
                  }
                };
              }
              return c;
            });
            return updated;
          });

          // Atualiza contadores globais
          updateConversations();
        };

        const handleMensagemDeletada = (data: { id?: string; apiMessageId?: string; ticketId: string; deletedAt: string }) => {
          // Atualizar lista de conversas quando mensagem for deletada
          setConversas(prev => {
            const updated = prev.map(c => {
              // Se for a conversa do ticket da mensagem deletada
              if (c.id === data.ticketId && c.ultimaMensagem) {
                // Verificar se a última mensagem foi a deletada
                const wasLastMessageDeleted = (
                  c.ultimaMensagem.id === data.id ||
                  c.ultimaMensagem.apiMessageId === data.apiMessageId ||
                  c.ultimaMensagem.evolutionMessageId === data.apiMessageId
                );

                if (wasLastMessageDeleted) {
                  // Buscar mensagem anterior ou marcar como sem mensagem
                  // Por enquanto, remove a última mensagem para forçar atualização
                  return {
                    ...c,
                    ultimaMensagem: undefined
                  };
                }
              }
              return c;
            });
            return updated;
          });
        };

        const handleMensagemEditada = (data: any) => {
          // Atualizar conteúdo da mensagem editada na lista de conversas
          setConversas(prev => {
            const updated = prev.map(c => {
              // Verificar se a conversa tem a mensagem editada como última mensagem
              if (c.ultimaMensagem && (
                c.ultimaMensagem.id === data.id ||
                c.ultimaMensagem.apiMessageId === data.apiMessageId ||
                c.ultimaMensagem.evolutionMessageId === data.apiMessageId
              )) {
                return {
                  ...c,
                  ultimaMensagem: {
                    ...c.ultimaMensagem,
                    conteudo: data.conteudo || c.ultimaMensagem.conteudo,
                    updatedAt: data.updatedAt
                  }
                };
              }
              return c;
            });
            return updated;
          });
        };

        const handleConversaRemovida = (data: { id: string; motivo: string; novoUsuarioId?: string }) => {
          setConversas(prev => prev.filter(c => c.id !== data.id));
          setConversaSelecionada(prev => (prev?.id === data.id ? null : prev));
        };

        const handleConversaFixada = (data: { id: string; fixada: boolean }) => {
          setConversas(prev => prev.map(c =>
            c.id === data.id ? { ...c, fixada: data.fixada } : c
          ));
        };

        const handleTicketCompartilhado = () => {
          updateConversations();
        };

        const handleContatoAtualizado = (data: any) => {
          const contato = data?.contato;
          if (!contato) return;
          setConversas(prev => prev.map(c => {
            const contatoId = c.contatoId || c.contato?.id;
            if (!contatoId || contatoId !== contato.id) return c;
            return {
              ...c,
              nome: renderEmojiWithFallback(contato.nome || contato.telefone || c.nome),
              avatar: contato.avatarUrl || c.avatar,
              contato: { ...(c.contato || {}), ...contato }
            };
          }));
        };

        socketService.on("chatListUpdated", handleChatListUpdated);
        socketService.on('conversa_atualizada', handleConversaAtualizada);
        socketService.on('contato_atualizado', handleContatoAtualizado);
        socketService.on('status_mensagem', handleStatusMensagem);
        socketService.on('mensagem_deletada', handleMensagemDeletada);
        socketService.on('mensagem_editada', handleMensagemEditada);
        socketService.on('conversa_removida', handleConversaRemovida);
        socketService.on('conversa_fixada_atualizada', handleConversaFixada);
        socketService.on('ticket_compartilhado', handleTicketCompartilhado);
        socketService.on('ticket_compartilhamento_removido', handleTicketCompartilhado);

        // Reconnect handler: re-join rooms and re-fetch data
        const handleReconnect = () => {
          if (user?.empresaId) {
            socketService.joinEmpresa(user.empresaId);
          }
          if (conversaSelecionada?.id) {
            socketService.joinConversa(conversaSelecionada.id);
          }
          updateConversations();
          fetchConversasFromBackend();
        };
        socketService.onReconnect(handleReconnect);

        // Save callbacks for cleanup
        socketCallbacksRef.current = {
          handleChatListUpdated,
          handleConversaAtualizada,
          handleContatoAtualizado,
          handleStatusMensagem,
          handleMensagemDeletada,
          handleMensagemEditada,
          handleConversaRemovida,
          handleConversaFixada,
          handleTicketCompartilhado,
          handleReconnect
        };

      } catch (error) {
        console.error('Error setting up socket listeners:', error);
        setSocketConnected(false);
      }
    };

    setupSocketListeners();

    return () => {
      // Clean up listeners
      if (socketCallbacksRef.current) {
        socketService.off('chatListUpdated', socketCallbacksRef.current.handleChatListUpdated);
        socketService.off('conversa_atualizada', socketCallbacksRef.current.handleConversaAtualizada);
        socketService.off('contato_atualizado', socketCallbacksRef.current.handleContatoAtualizado);
        socketService.off('status_mensagem', socketCallbacksRef.current.handleStatusMensagem);
        socketService.off('mensagem_deletada', socketCallbacksRef.current.handleMensagemDeletada);
        socketService.off('mensagem_editada', socketCallbacksRef.current.handleMensagemEditada);
        socketService.off('conversa_removida', socketCallbacksRef.current.handleConversaRemovida);
        socketService.off('conversa_fixada_atualizada', socketCallbacksRef.current.handleConversaFixada);
        socketService.off('ticket_compartilhado', socketCallbacksRef.current.handleTicketCompartilhado);
        socketService.off('ticket_compartilhamento_removido', socketCallbacksRef.current.handleTicketCompartilhado);
        socketService.offReconnect(socketCallbacksRef.current.handleReconnect);
      }
      setSocketConnected(false);
    };
  }, [user, showNotification]);

  // Effect to join conversation room when conversation is selected
  useEffect(() => {
    if (socketConnected && conversaSelecionada?.id) {
      console.log('Joining conversation room:', conversaSelecionada.id);
      socketService.joinConversa(conversaSelecionada.id);
    }
  }, [conversaSelecionada?.id, socketConnected]);

  // --- Filtragem e Ordenação (Memoizado) ---
  const filteredConversas = useMemo(() => {
    let filtered = conversas.filter(c => c.statusTicket === activeTab);

    // Filtrar mensagens de grupo se necessário
    if (!showGroupMessages) {
      filtered = filtered.filter(conversa =>
        !conversa.ultimaMensagem?.isGrupo
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (conversa) =>
          conversa.nome.toLowerCase().includes(term) ||
          conversa.remoteJid.includes(term) ||
          (conversa.ultimaMensagem?.conteudo?.toLowerCase().includes(term)) ||
          (conversa.atendente?.nome?.toLowerCase().includes(term)) ||
          (conversa.instancia?.nome?.toLowerCase().includes(term))
      );
    }
    // Ordenar: fixadas primeiro, depois por dataAtualizacao decrescente
    return filtered.sort((a, b) => {
      if (a.fixada && !b.fixada) return -1;
      if (!a.fixada && b.fixada) return 1;
      const dateA = a.dataAtualizacao instanceof Date ? a.dataAtualizacao : new Date(a.dataAtualizacao);
      const dateB = b.dataAtualizacao instanceof Date ? b.dataAtualizacao : new Date(b.dataAtualizacao);
      return dateB.getTime() - dateA.getTime();
    });
  }, [conversas, searchTerm, activeTab, showGroupMessages]);

  // Contadores dinâmicos baseados na busca e filtro de grupos
  const counts = useMemo(() => {
    return { aberto: countAbertos, pendente: countPendentes, fechado: countFechados };
  }, [countAbertos, countPendentes, countFechados, searchTerm, showGroupMessages]); // Adicionado searchTerm de volta

  // --- Notificações --- 
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast({ title: t('common.error'), description: t('dashboard.desktopNotificationsUnsupported'), variant: "destructive" });
      return false;
    }
    if (Notification.permission === 'granted') {
      return true;
    }
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  };

  // Solicitar permissão ao carregar (opcional, pode ser feito sob demanda)
  // useEffect(() => {
  //   requestNotificationPermission();
  // }, []);

  // --- Navegação e Ações ---
  // Abre a conversa na própria Dashboard, exibindo AtendimentoPage na área principal
  const handleOpenConversa = (chatResumo: ConversaResumo) => {
    // Limpa qualquer mensagem alvo do localStorage para evitar navegação indesejada
    localStorage.removeItem('targetMessageId');
    localStorage.removeItem('targetApiMessageId');
    localStorage.removeItem('targetEvolutionMessageId');

    setConversaSelecionada(chatResumo);
    setConversas(prev => prev.map(c => c.id === chatResumo.id ? { ...c, naoLidas: 0 } : c));
  };

  const getInitials = (name: string | null | undefined = '') => {
    return (name ?? '')
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'C';
  };

  const getFileUrl = (path?: string) => {
    if (!path) return '';
    if (/^https?:\/\//.test(path)) return path;
    let API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
    if (!path.startsWith('/')) path = '/' + path;
    if (!path.startsWith('/uploads')) path = '/uploads' + path;
    return `${API_URL}${path}`;
  };

  const handleConversationClick = async (conversa: ConversaResumo) => {
    try {
      console.log('Dashboard recebendo atualização:', conversa);

      // Atualizar imediatamente a conversa na lista local
      setConversas(prev => {
        const idx = prev.findIndex(c => c.id === conversa.id);
        let novasConversas;
        if (idx > -1) {
          novasConversas = [...prev];
          novasConversas[idx] = { ...prev[idx], ...conversa };
        } else {
          novasConversas = [conversa, ...prev];
        }
        return novasConversas.sort((a, b) => {
          if (a.fixada && !b.fixada) return -1;
          if (!a.fixada && b.fixada) return 1;
          const dateA = a.dataAtualizacao instanceof Date ? a.dataAtualizacao : new Date(a.dataAtualizacao);
          const dateB = b.dataAtualizacao instanceof Date ? b.dataAtualizacao : new Date(b.dataAtualizacao);
          return dateB.getTime() - dateA.getTime();
        });
      });

      // Save the current conversation ID
      const currentConversationId = conversaSelecionada?.id;

      // Check if we need to navigate to "abertos" tab based on status
      if (conversa.statusTicket === 'aberto' || conversa.statusTicket === 'fechado') {
        setActiveTab('aberto');
      }

      // If the same conversation is still in the list, select it again
      if (currentConversationId === conversa.id) {
        // É a mesma conversa que está sendo atualizada - usar os dados recebidos
        setConversaSelecionada(conversa);
        setShowChat(true);
        return;
      }

      // If the same conversation is still in the list, select it again
      if (currentConversationId) {
        const updatedConversation = conversas.find(c => c.id === currentConversationId);
        if (updatedConversation) {
          setConversaSelecionada(updatedConversation);
          setShowChat(true);
          return;
        }
      }

      // If not found or no previous conversation, proceed with the new selection
      // Only select if status is not 'fechado' (closed conversations shouldn't be auto-selected)
      if (conversa.statusTicket !== 'fechado') {
        setConversaSelecionada(conversa);
        setShowChat(true);
      } else {
        // If conversation is closed, just refresh the list and stay on dashboard
        setConversaSelecionada(null);
        setShowChat(false);
      }
    } catch (error) {
      console.error('Erro ao atualizar conversa:', error);
      toast({ title: t('common.error'), description: t('dashboard.loadConversationError'), variant: 'destructive' });
    }
  };

  // --- Renderização do Item da Lista de Conversas (AJUSTADO) ---
  // --- Handlers para ConversaListItem ---
  const handleStartAtendimento = useCallback(async (conversa: ConversaResumo) => {
    setStartingAttendance(true);
    try {
      await api.put(`/chats/${conversa.id}/status?status=aberto`);
      toast({ title: t('dashboard.attendanceStarted'), variant: 'default' });

      setConversaSelecionada(prev => prev?.id === conversa.id
        ? { ...prev, statusTicket: 'aberto', status: 'aberto' }
        : prev
      );

      if (conversa.statusTicket === 'pendente') {
        setCountPendentes(prev => Math.max(0, prev - 1));
        setCountAbertos(prev => prev + 1);
      }

      socketService.emit('chatListUpdated');

      setTimeout(() => {
        setActiveTab('aberto');
      }, 1500);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('dashboard.startAttendanceError'),
        variant: 'destructive',
      });
    } finally {
      setStartingAttendance(false);
    }
  }, [setConversaSelecionada, setCountAbertos, setCountPendentes, setStartingAttendance, setActiveTab]);

  const handleFinishAtendimento = useCallback(async (conversa: ConversaResumo) => {
    try {
      await api.put(`/chats/${conversa.id}/status?status=fechado`);
      toast({ title: t('dashboard.attendanceFinished'), variant: 'default' });

      setConversas(prev => {
        const idx = prev.findIndex(c => c.id === conversa.id);
        if (idx > -1) {
          const novasConversas = [...prev];
          novasConversas[idx] = {
            ...novasConversas[idx],
            statusTicket: 'fechado',
            status: 'fechado'
          };
          return novasConversas;
        }
        return prev;
      });

      setCountAbertos(prev => Math.max(0, prev - 1));
      setCountFechados(prev => prev + 1);

      setConversaSelecionada(prev => prev?.id === conversa.id
        ? { ...prev, statusTicket: 'fechado', status: 'fechado' }
        : prev
      );

      setTimeout(() => {
        setConversaSelecionada(prev => prev?.id === conversa.id ? null : prev);
      }, 500);

      socketService.emit('chatListUpdated');
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('dashboard.finishAttendanceError'),
        variant: 'destructive',
      });
    }
  }, [setConversas, setCountAbertos, setCountFechados, setConversaSelecionada]);

  const handleReopenAtendimento = useCallback(async (conversa: ConversaResumo) => {
    try {
      await api.put(`/chats/${conversa.id}/status?status=aberto`);
      toast({ title: t('dashboard.attendanceReopened'), variant: 'default' });

      setConversas(prev => {
        const idx = prev.findIndex(c => c.id === conversa.id);
        if (idx > -1) {
          const novasConversas = [...prev];
          novasConversas[idx] = {
            ...novasConversas[idx],
            statusTicket: 'aberto',
            status: 'aberto'
          };
          return novasConversas;
        }
        return prev;
      });

      setCountFechados(prev => Math.max(0, prev - 1));
      setCountAbertos(prev => prev + 1);

      socketService.emit('chatListUpdated');

      setTimeout(() => {
        setActiveTab('aberto');
      }, 1500);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('dashboard.reopenAttendanceError'),
        variant: 'destructive',
      });
    }
  }, [setConversas, setCountFechados, setCountAbertos, setActiveTab]);

  const handleTransferClick = useCallback((conversa: ConversaResumo) => {
    setTransferTarget(conversa);
    setTransferToUser('');
    setTransferToQueue('');
    loadTransferData();
    setShowTransferModal(true);
  }, [setTransferTarget, setTransferToUser, setTransferToQueue, loadTransferData, setShowTransferModal]);

  const handlePinToggle = useCallback(async (conversa: ConversaResumo) => {
    try {
      await api.patch(`/chats/${conversa.id}/fixar`);
      setConversas(prev => prev.map(c =>
        c.id === conversa.id ? { ...c, fixada: !c.fixada } : c
      ));
    } catch (err) {
      toast({ title: t('common.error'), description: t('dashboard.pinConversationError'), variant: 'destructive' });
    }
  }, [setConversas]);

  // --- Renderização Principal ---
  return (
    <div className="flex h-[100dvh] bg-gray-100 dark:bg-gray-900 overflow-hidden">
      {/* --- Barra Lateral --- */}
      <aside
        className={`transition-all duration-300 bg-[#f0f2f5] dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 flex flex-col h-screen
          ${conversaSelecionada ? "hidden md:flex" : "flex w-full"}
          ${sidebarCollapsed
            ? "md:w-[80px] md:min-w-[80px] md:max-w-[80px]"
            : "md:w-[30%] md:max-w-[420px] md:min-w-[300px]"
          }`}
      >
        {/* Cabeçalho da Barra Lateral */}
        <header className="bg-[#f0f2f5] dark:bg-slate-800 p-3 h-[60px] flex items-center justify-between border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
          {/* Logo e Nome da Empresa */}
          <div className="flex items-center ml-2">
            <img src={logo ? `${API_BASE_URL}${logo.startsWith('/') ? '' : '/'}${logo}` : SintisIcon} alt="logo" className="h-10 w-30" />
            {!sidebarCollapsed && nomeEmpresa && (
              <span className="ml-3 text-lg font-semibold text-gray-800 dark:text-gray-200 truncate">
                {nomeEmpresa}
              </span>
            )}
          </div>
          {/* Botões de Ação */}
          <div
            className={`transition-all duration-300 flex items-center space-x-1 text-gray-600 dark:text-gray-400 ${sidebarCollapsed
              ? "opacity-0 pointer-events-none hidden"
              : "opacity-100 pointer-events-auto visible"
              }`}
          >
            {/* Botão Novo Atendimento */}
            {!sidebarCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                title={t('dashboard.newAttendance')}
                onClick={() => setIsNewChatDialogOpen(true)}
              >
                <MessageSquarePlus size={20} />
              </Button>
            )}
            <div className="md:hidden flex items-center">
              <Header titulo="" user={user} variant="mobile" />
            </div>
          </div>
        </header>

        {/* Barra de Pesquisa e Filtro */}
        <div
          className={`transition-all duration-300 flex space-x-1 text-gray-600 dark:text-gray-400 w-100 justify-center ${sidebarCollapsed
            ? "opacity-100 pointer-events-auto visible"
            : "opacity-0 pointer-events-none hidden"
            }`}
        >
          {/* Botão Novo Atendimento */}
          {sidebarCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
              title={t('dashboard.newAttendance')}
              onClick={() => setIsNewChatDialogOpen(true)}
            >
              <MessageSquarePlus size={20} />
            </Button>
          )}
        </div>
        <div
          className={`transition-all duration-300 p-4 bg-[#f0f2f5] dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex-shrink-0 ${sidebarCollapsed
            ? "opacity-0 pointer-events-none hidden"
            : "opacity-100 pointer-events-auto visible"
            }`}
        >
          <div className="relative flex items-center w-full gap-1">
            <div className="relative flex items-center flex-1 bg-white dark:bg-slate-700 rounded-lg overflow-hidden h-9">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 z-10" />

              {/* Custom Marquee Placeholder */}
              {!searchTerm && (
                <div className="absolute left-10 right-8 top-0 bottom-0 pointer-events-none overflow-hidden flex items-center">
                  <span className="search-input-marquee-text text-gray-400 dark:text-gray-500 text-sm whitespace-nowrap">
                    {t('dashboard.searchPlaceholder')}
                  </span>
                </div>
              )}

              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-full bg-transparent text-gray-900 dark:text-white pl-10 pr-8 py-1.5 text-sm border-transparent focus:border-transparent focus:ring-0 z-20 relative"
              />

              {/* Clear Search Button */}
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setShowMessagesSearch(false);
                    setSearchMessagesResults([]);
                    setSearchMessagesError(null);
                  }}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-full h-7 w-7 p-0 z-30"
                  title={t('dashboard.clearSearch')}
                >
                  <XCircle size={14} />
                </Button>
              )}
            </div>

            {/* Group Filter Toggle */}
            <div className="flex items-center">
              <Button
                variant={showGroupMessages ? "default" : "outline"}
                size="sm"
                onClick={() => setShowGroupMessages(!showGroupMessages)}
                className={`flex items-center gap-1 h-8 px-2 text-xs relative overflow-hidden group ${showGroupMessages
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
                  }`}
                title={showGroupMessages ? t('dashboard.hideGroupMessages') : t('dashboard.showGroupMessages')}
              >
                <Users size={14} />
                <span className="max-w-0 overflow-hidden transition-all duration-300 group-hover:max-w-[60px] group-hover:ml-1">
                  {showGroupMessages ? t('dashboard.groups') : t('dashboard.private')}
                </span>
              </Button>
            </div>

            {/* Unread Messages Filter Toggle */}
            <div className="flex items-center ml-1">
              <Button
                variant={showUnreadOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                className={`flex items-center gap-1 h-8 px-2 text-xs relative overflow-hidden group ${showUnreadOnly
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
                  }`}
                title={showUnreadOnly ? t('dashboard.showAllMessages') : t('dashboard.showUnreadOnly')}
              >
                <MessageSquare size={14} />
                <span className="max-w-0 overflow-hidden transition-all duration-300 group-hover:max-w-[70px] group-hover:ml-1">
                  {showUnreadOnly ? t('dashboard.unread') : t('dashboard.all')}
                </span>
              </Button>
            </div>

            {/* Tags Filter */}
            {chatConfig?.permitirTags && availableTags.length > 0 && (
              <div className="flex items-center ml-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`flex items-center gap-1 h-8 px-2 text-xs relative overflow-hidden group ${selectedTags.length > 0
                        ? 'bg-purple-500 hover:bg-purple-600 text-white border-purple-500'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
                        }`}
                      title={t('dashboard.filterByTags')}
                    >
                      <Tag size={14} />
                      <span className="max-w-0 overflow-hidden transition-all duration-300 group-hover:max-w-[80px] group-hover:ml-1">
                        {selectedTags.length > 0 ? `${t('dashboard.tags')} (${selectedTags.length})` : t('dashboard.tags')}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 max-h-64 overflow-y-auto">
                    <DropdownMenuLabel>{t('dashboard.filterByTags')}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {availableTags.map((tag: any) => (
                      <DropdownMenuItem
                        key={tag.id}
                        onClick={() => {
                          setSelectedTags(prev =>
                            prev.includes(tag.id)
                              ? prev.filter(id => id !== tag.id)
                              : [...prev, tag.id]
                          );
                        }}
                        className="flex items-center justify-between cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full border-2"
                            style={{
                              backgroundColor: tag.cor || '#3B82F6',
                              borderColor: tag.cor || '#3B82F6'
                            }}
                          />
                          <span className="text-sm">{tag.titulo}</span>
                          {tag.prioridade && (
                            <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1 rounded">
                              {tag.prioridade}
                            </span>
                          )}
                        </div>
                        {selectedTags.includes(tag.id) && (
                          <span className="text-purple-500 text-xs">✓</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                    {selectedTags.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setSelectedTags([])}
                          className="text-red-600 cursor-pointer"
                        >
                          {t('dashboard.clearFilters')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {chatConfig?.permitirKanban && (
              <div className="flex items-center ml-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')}
                  className={`flex items-center gap-1 h-8 px-2 text-xs relative overflow-hidden group ${
                    viewMode === 'kanban'
                      ? 'bg-orange-500 hover:bg-orange-600 text-white border-orange-500'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
                  }`}
                  title={viewMode === 'kanban' ? t('dashboard.backToList') : t('dashboard.viewKanban')}
                >
                  <LayoutGrid size={14} />
                  <span className="max-w-0 overflow-hidden transition-all duration-300 group-hover:max-w-[80px] group-hover:ml-1">
                    Kanban
                  </span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Abas de Filtro */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}
          className={`transition-all duration-300 w-full p-0 flex-shrink-0 ${sidebarCollapsed
            ? "flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 h-30"
            : "h-10"
            }`}
        >
          <TabsList className={`w-full grid p-0 rounded-none bg-[#f0f2f5] dark:bg-slate-800 ${sidebarCollapsed
            ? "grid-rows-3 h-30"
            : "grid-cols-3 h-10"}`}>
            <TabsTrigger
              value="aberto"
              className={`text-xs rounded-none relative h-full transition-colors
                ${activeTab === 'aberto'
                  ? 'bg-white dark:bg-slate-900 font-semibold text-green-700 dark:text-green-400 shadow border-b-2 border-green-600'
                  : 'text-gray-600 dark:text-gray-300'
                } ${sidebarCollapsed ? " w-20 h-10" : ""}`}
            >
              {sidebarCollapsed ? (
                <span className="absolute inline-block px-1.5 py-1 text-[11px] leading-none text-white transform bg-green-600 rounded-full">
                  {counts.aberto}
                </span>
              ) : (
                <div>
                  <span>{t('dashboard.open')}</span>
                  <span className="absolute right-0 inline-block px-1.5 py-1 text-[11px] leading-none text-white transform bg-green-600 rounded-full mr-1">
                    {counts.aberto}
                  </span>
                </div>
              )}

            </TabsTrigger>
            <TabsTrigger
              value="pendente"
              className={`text-xs rounded-none relative h-full transition-colors
                ${activeTab === 'pendente'
                  ? 'bg-white dark:bg-slate-900 font-semibold text-orange-700 dark:text-orange-400 shadow border-b-2 border-orange-600'
                  : 'text-gray-600 dark:text-gray-300'
                } ${sidebarCollapsed ? " w-20 h-10" : ""}`}
            >
              {sidebarCollapsed ? (
                <span className="absolute inline-block px-1.5 py-1 text-[11px] leading-none text-white transform bg-orange-600 rounded-full">
                  {counts.pendente}
                </span>
              ) : (
                <div>
                  <span>{t('dashboard.pending')}</span>
                  <span className="absolute right-0 inline-block px-1.5 py-1 text-[11px] leading-none text-white transform bg-orange-600 rounded-full mr-1">
                    {counts.pendente}
                  </span>
                </div>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="fechado"
              className={`text-xs rounded-none relative h-full transition-colors
                ${activeTab === 'fechado'
                  ? 'bg-white dark:bg-slate-900 font-semibold text-red-700 dark:text-red-400 shadow border-b-2 border-red-600'
                  : 'text-gray-600 dark:text-gray-300'
                } ${sidebarCollapsed ? " w-20 h-10" : ""}`}
            >
              {sidebarCollapsed ? (
                <span className="absolute inline-block px-1.5 py-1 text-[11px] leading-none text-white transform bg-red-600 rounded-full">
                  {counts.fechado}
                </span>
              ) : (
                <div>
                  <span>{t('dashboard.closed')}</span>
                  <span className="absolute right-0 inline-block px-1.5 py-1 text-[11px] leading-none text-white transform bg-red-600 rounded-full mr-1">
                    {counts.fechado}
                  </span>
                </div>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Lista de Conversas ou Kanban */}
        {viewMode === 'kanban' ? (
          <div className="flex-1 bg-white dark:bg-slate-900 overflow-hidden">
            <KanbanBoard
              className="h-full"
              onTicketClick={(ticket) => {
                setViewMode('list');
                const conversa = conversas.find(c => c.id === ticket.id);
                if (conversa) handleOpenConversa(conversa);
              }}
            />
          </div>
        ) : (
        <ScrollArea className="flex-1 bg-white dark:bg-slate-900">
          {loading && filteredConversas.length === 0 ? (
            <div className="flex items-center justify-center h-full m-4">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </div>
          ) : error ? (
            <div>
              {sidebarCollapsed ? (
                <div className="flex flex-col items-center justify-center h-full text-red-500 p-4">
                  <AlertCircle size={32} className="mb-2" />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-red-500 p-4">
                  <AlertCircle size={32} className="mb-2" />
                  <p className="text-center text-sm">{error}</p>
                  <Button onClick={() => fetchConversasFromBackend()} variant="outline" size="sm" className="mt-4">{t('dashboard.retry')}</Button>
                </div>
              )}
            </div>
          ) : filteredConversas.length === 0 ? (
            <div>
              {sidebarCollapsed ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 p-4">
                  <MessageSquare size={30} className="opacity-50 mb-2" />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 p-4">
                  <MessageSquarePlus size={48} className="opacity-50 mb-2" />
                  <p className="mt-2 text-center text-sm">{t('dashboard.noConversations')}</p>
                  {searchTerm && <p className="mt-1 text-center text-xs text-gray-400">{t('dashboard.refineSearch')}</p>}
                  {!searchTerm && <p className="mt-1 text-center text-xs text-gray-400">{t('dashboard.checkOtherTabs')}</p>}
                </div>
              )}
            </div>
          ) : (
            <VirtualizedConversaList
              conversas={filteredConversas}
              itemRenderer={(conversa) => (
                <ConversaListItem
                  key={conversa.id}
                  conversa={conversa}
                  chatConfig={chatConfig}
                  user={user}
                  startingAttendance={startingAttendance}
                  onOpenConversa={handleOpenConversa}
                  onStartAtendimento={handleStartAtendimento}
                  onFinishAtendimento={handleFinishAtendimento}
                  onReopenAtendimento={handleReopenAtendimento}
                  onTransferClick={handleTransferClick}
                  onPinToggle={handlePinToggle}
                />
              )}
              isLoadingMore={loading}
              hasMore={hasMore}
              loadMore={() => {
                const nextPage = currentPageRef.current + 1;
                console.log('[Dashboard] loadMore triggered, currentPage:', currentPageRef.current, 'nextPage:', nextPage);
                fetchConversasFromBackend(nextPage, true);
              }}
            />
          )}
        </ScrollArea>
        )}

        {/* Resultados da busca de mensagens - Fundo branco */}
        {showMessagesSearch && (
          <div className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700">
            <div className="p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 font-medium">{t('dashboard.messageSearchResults')}</div>
              {searchMessagesError && (
                <div className="text-red-500 text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded">{searchMessagesError}</div>
              )}
              {!searchMessagesLoading && searchMessagesResults.length === 0 && searchTerm && !searchMessagesError && (
                <div className="text-gray-400 text-sm py-8 text-center bg-gray-50 dark:bg-slate-800 rounded">{t('dashboard.noMessagesFound')}</div>
              )}
              <ScrollArea
                className="flex-1"
                onScroll={(e) => {
                  const element = e.currentTarget;
                  if (element.scrollTop + element.clientHeight >= element.scrollHeight - 100) {
                    loadMoreSearchMessages();
                  }
                }}
              >
                <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
                  {searchMessagesResults.map((msg, idx) => (
                    <div
                      key={msg.id}
                      className="p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer border border-gray-200 dark:border-slate-700 transition-colors"
                      onClick={() => handleOpenConversationFromMessage(msg)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{msg.contato?.nome || t('dashboard.unknown')}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{format(new Date(msg.timestamp), "dd/MM/yyyy HH:mm")}</span>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        {highlightTerm(msg.texto || (msg.tipo === 'texto' ? msg.conteudo : msg.caption) || '', searchTerm)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Botão Carregar Mais */}
                {searchMessagesPagination.hasMore && !searchMessagesLoading && (
                  <div className="p-4 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadMoreSearchMessages}
                      className="w-full dark:text-white"
                    >
                      {t('dashboard.loadMoreMessages')}
                    </Button>
                  </div>
                )}

                {/* Indicador de carregamento */}
                {searchMessagesLoading && (
                  <div className="p-4 text-center">
                    <span className="text-gray-400">{t('dashboard.loadingMoreMessages')}</span>
                  </div>
                )}
        </ScrollArea>
            </div>
          </div>
        )}
      </aside>

      {/* Modal Novo Chat */}
      <NovoChatModal
        open={isNewChatDialogOpen}
        onOpenChange={handleNewChatDialogChange}
        selectedInstance={selectedInstance}
        onInstanceChange={setSelectedInstance}
        availableInstances={availableInstances}
        modalContatoSearch={modalContatoSearch}
        onContatoSearchChange={(value) => {
          setModalContatoSearch(value);
          if (/^\d*$/.test(value)) {
            setNewChatJid(value);
          }
        }}
        modalContatosLoading={modalContatosLoading}
        modalContatos={modalContatos}
        onSelectContato={handleStartNewChatFromContato}
        onStartNewChat={handleStartNewChat}
      />

      {/* Modal de Transferência (Dashboard) */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-[500px] dark:bg-slate-800 dark:text-white">
          <DialogHeader>
            <DialogTitle>{t('dashboard.transferTicket')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.transferDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{t('dashboard.transferToUser')}</Label>
              <SelectWithSearch
                options={availableUsers
                  .filter((u: any) => u.id)
                  .map((u: any) => ({ value: u.id, label: `${u.nome} (${u.email})` }))}
                value={transferToUser ? [{ value: transferToUser, label: '' }] : []}
                onChange={(val) => {
                  const id = val[0]?.value || '';
                  setTransferToUser(id);
                  if (id) setTransferToQueue('');
                }}
                placeholder={t('dashboard.selectUserPlaceholder')}
                loading={loadingTransfer}
                disabled={loadingTransfer}
                single
              />
            </div>
            <div className="flex items-center justify-center text-sm text-gray-500">{t('dashboard.or')}</div>
            <div className="grid gap-2">
              <Label>{t('dashboard.transferToQueue')}</Label>
              <SelectWithSearch
                options={availableQueues.map((f: any) => ({ value: f.id, label: f.nome }))}
                value={transferToQueue ? [{ value: transferToQueue, label: '' }] : []}
                onChange={(val) => {
                  const id = val[0]?.value || '';
                  setTransferToQueue(id);
                  if (id) setTransferToUser('');
                }}
                placeholder={t('dashboard.selectQueuePlaceholder')}
                loading={loadingTransfer}
                disabled={loadingTransfer}
                single
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferToUser('');
                  setTransferToQueue('');
                  setTransferTarget(null);
                }}
              >
                {t('common.cancel')}
              </Button>
            </DialogClose>
            <Button
              type="button"
              disabled={!transferToUser && !transferToQueue}
              onClick={async () => {
                if (!transferTarget?.id) return;
                if (!transferToUser && !transferToQueue) {
                  toast({ title: t('common.error'), description: t('dashboard.selectUserOrQueue'), variant: 'destructive' });
                  return;
                }
                try {
                  if (transferToUser) {
                    await api.post('/tickets/transfer', {
                      ticketId: transferTarget.id,
                      novoUsuarioId: transferToUser,
                      novaFilaId: null,
                    });
                  } else if (transferToQueue) {
                    await api.patch(`/chats/${transferTarget.id}/fila`, { filaId: transferToQueue });
                  }

                  toast({ title: t('common.success'), description: t('dashboard.ticketTransfered') });

                  // Fechar modal e limpar campos
                  setShowTransferModal(false);
                  setTransferToUser('');
                  setTransferToQueue('');
                  const transferredId = transferTarget.id;
                  setTransferTarget(null);

                  // Atualizar listas e contadores conforme a aba atual
                  await fetchConversasFromBackend();

                  // Se a conversa aberta for a transferida, fecha a tela de atendimento
                  setConversaSelecionada(prev => (prev?.id === transferredId ? null : prev));
                } catch (error: any) {
                  toast({
                    title: t('common.error'),
                    description: error?.response?.data?.message || t('dashboard.ticketTransferError'),
                    variant: 'destructive',
                  });
                }
              }}
            >
              {t('dashboard.transfer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Área Principal (Placeholder ou Rota Aninhada) --- */}
      <main className={`flex-col bg-[#f0f2f5] dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700 flex-1 min-w-0 min-h-0 overflow-hidden
        ${conversaSelecionada ? "flex w-full" : "hidden md:flex"}
      `}>
        {/* Header Fixo (se necessário, ou usar um layout aninhado) */}
        <div className="hidden md:block">
          <Header titulo="Sinticket" user={user} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed} />
        </div>

        {/* Header Mobile para AtendimentoPage */}
        {conversaSelecionada && (
          <header className="md:hidden bg-[#f0f2f5] dark:bg-slate-800 p-3 h-[60px] flex items-center justify-between border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <img src={logo ? `${API_BASE_URL}${logo.startsWith('/') ? '' : '/'}${logo}` : SintisIcon} alt="logo" className="h-10 w-auto" />
              <h1 className="text-lg font-bold text-gray-800 dark:text-white truncate max-w-[150px]">{user?.empresa?.nome || "Sinticket"}</h1>
            </div>
            <div className="flex items-center">
              <Header titulo="" user={user} variant="mobile" />
            </div>
          </header>
        )}

        {conversaSelecionada ? (
          <AtendimentoPage refresh={handleConversationClick} chatResumo={conversaSelecionada} setChatResumo={setConversaSelecionada} setStartChat={handleStartNewChatFromContato} />
        ) : (
          <div className="flex-1 flex items-center justify-center p-10 relative">
            <img src={SintisLogo} alt="Sintis" className="absolute top-0 left-0 opacity-50 max-w-lg" />
            <div className="text-center max-w-md">
              <h1 className="text-2xl text-gray-500 dark:text-gray-400 font-light mb-3">{t('dashboard.selectConversation')}</h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">
                {t('dashboard.chooseAttendance')}
              </p>
            </div>
          </div>
        )}
        {startingAttendance && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 flex items-center space-x-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="text-gray-700 dark:text-gray-300">{t('dashboard.startingAttendance')}</span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
